import { css } from '@emotion/react';
import { InvoiceFilterType } from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Chip, PopoverDialog, SwitchChipGroup, useFormField, usePopoverDialog } from '@frontend/design-system';
import { CustomDatePicker } from '../../QuickFill/custom-date-picker';

const styles = {
  chip: css`
    cursor: pointer;
    color: ${theme.colors.neutral90};
    &:hover {
      background: ${theme.colors.neutral10};
      border: solid 1px ${theme.colors.neutral10};
    }
  `,
  label: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  chipSelected: css`
    border: solid 1px ${theme.colors.primary50};
    background: ${theme.colors.primary10};
    &:hover {
      background: ${theme.colors.primary10};
      border: solid 1px ${theme.colors.primary50};
    }
  `,
  datePicker: css`
    max-width: calc(400px - 32px);
    padding: ${theme.spacing(3)} ${theme.spacing(2)};
  `,
  switchChipGroupWrapperOnly: css`
    padding: 0;
    margin: 0;
    border: none;
    background: none;
  `,
};

type CustomDateFilterChipProps = {
  onSelect: (range: string[]) => void;
  isSelected: boolean;
  dateRange: InvoiceFilterType['dateRange'];
};
export const CustomDateFilterChip = ({ onSelect, isSelected, dateRange }: CustomDateFilterChipProps) => {
  const { t } = useTranslation('payments');
  const dateRangeProps = useFormField({
    type: 'dateRange',
    value: isSelected && dateRange?.start && dateRange?.end ? [dateRange.start, dateRange.end] : [],
  });
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { getDialogProps, getTriggerProps, close } = usePopoverDialog({
    placement: 'bottom-end',
  });

  return (
    <SwitchChipGroup.Option value={'custom:wrapper-unused'} css={styles.switchChipGroupWrapperOnly}>
      <Chip.DropdownFilter
        trackingId={`pay-portal-invoicelist-quick-range-btn-custom`}
        css={[styles.chip, isSelected && styles.chipSelected]}
        className={`custom-date-filter-chip ${isSelected ? 'selected' : ''}`}
        {...getTriggerProps()}
      >
        <span css={styles.label}>{isMobile ? <Icon name='calendar-small' /> : t('Custom')}</span>
      </Chip.DropdownFilter>
      <PopoverDialog {...getDialogProps()}>
        <CustomDatePicker
          css={styles.datePicker}
          dateRangeProps={dateRangeProps}
          closePicker={() => close()}
          onSelect={onSelect}
        />
      </PopoverDialog>
    </SwitchChipGroup.Option>
  );
};

/**
 * SwitchGripGroup expects its children to be of type SwitchChip
 */
CustomDateFilterChip.displayName = 'SwitchChip';
